import * as THREE from 'three';
import type { Viewer } from '../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Viewer-Factory/Viewer';
import { useViewerStore } from '../store/store';

/** True when the 3D roof model has been drawn and is large enough to snapshot. */
export function isSiteModelReadyForSnapshot(viewer: Viewer | null | undefined): boolean {
    if (!viewer?.roofGroup) return false;

    const roofs = viewer.roofs;
    if (Array.isArray(roofs) && roofs.length > 0 && (roofs[0]?.faces?.length ?? 0) > 0) {
        return true;
    }

    const boundingBox = new THREE.Box3().setFromObject(viewer.roofGroup);
    const size = new THREE.Vector3();
    boundingBox.getSize(size);
    return size.x > 0.1 && size.z > 0.1;
}

/** Reject missing/blank data URLs produced before the site model finished loading. */
export function isValidSnapshotDataUrl(url: string | null | undefined): boolean {
    if (!url || typeof url !== 'string') return false;
    if (!/^data:image\/(png|jpeg|jpg|webp);base64,/i.test(url)) return false;
    const base64 = url.split(',')[1] ?? '';
    return base64.length > 500;
}

/** Looser check for thumbnails restored from DB / IndexedDB (may be smaller JPEGs). */
export function isUsablePersistedSnapshotUrl(url: string | null | undefined): boolean {
    if (!url || typeof url !== 'string') return false;
    const trimmed = url.trim();
    if (!trimmed) return false;
    if (/^https?:\/\//i.test(trimmed)) return true;
    if (/^data:image\//i.test(trimmed)) {
        const base64 = trimmed.split(',')[1] ?? '';
        return base64.length > 80;
    }
    if (/^[A-Za-z0-9+/=]{80,}$/.test(trimmed)) return true;
    return false;
}

/** Normalize persisted snapshot strings from DB / cache (data URLs, bare base64, or http URLs). */
export function normalizePersistedSnapshotUrl(url: string | null | undefined): string | undefined {
    if (!url || typeof url !== 'string') return undefined;
    const trimmed = url.trim();
    if (!trimmed) return undefined;
    if (isValidSnapshotDataUrl(trimmed)) return trimmed;
    if (/^https?:\/\//i.test(trimmed) && isUsablePersistedSnapshotUrl(trimmed)) return trimmed;
    if (/^data:image\//i.test(trimmed) && isUsablePersistedSnapshotUrl(trimmed)) return trimmed;
    if (/^[A-Za-z0-9+/=]{80,}$/.test(trimmed)) {
        const asDataUrl = `data:image/jpeg;base64,${trimmed}`;
        return isUsablePersistedSnapshotUrl(asDataUrl) ? asDataUrl : undefined;
    }
    return undefined;
}

/** Capture a top-down orthographic layout snapshot with readiness checks and validation. */
export async function captureOrthoSnapshotFromViewer(
    viewer: { getSquareImageOfSite?: (isMeasureLayout?: boolean, brighterPdfCapture?: boolean) => string } | null | undefined,
): Promise<{ url?: string; error?: string }> {
    if (!viewer || typeof viewer.getSquareImageOfSite !== 'function') {
        return { error: '3D viewer not available' };
    }
    if (!isSiteModelReadyForSnapshot(viewer as Viewer)) {
        return { error: 'Site model not ready for snapshot' };
    }
    await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
    try {
        const img = viewer.getSquareImageOfSite(false);
        if (isValidSnapshotDataUrl(img)) {
            return { url: img };
        }
        if (img) {
            return { error: 'Snapshot image was blank or too small' };
        }
        return { error: 'Snapshot capture returned no image' };
    } catch (e) {
        return { error: e instanceof Error ? e.message : 'Snapshot capture failed' };
    }
}

/** Max edge length for layout thumbnails persisted to remote AI result storage. */
const SAVE_SNAPSHOT_MAX_EDGE_PX = 800;
/** JPEG quality for persisted layout thumbnails (PNG captures are ~4MB+ each at 2000px). */
const SAVE_SNAPSHOT_JPEG_QUALITY = 0.82;

/**
 * Downscale and re-encode a layout snapshot for persistence.
 * Full 2000×2000 PNG data URLs can exhaust browser memory when many are JSON.stringified together.
 */
export function compressSnapshotDataUrlForSave(dataUrl: string): Promise<string> {
    if (!isValidSnapshotDataUrl(dataUrl)) {
        return Promise.resolve(dataUrl);
    }
    if (typeof document === 'undefined') {
        return Promise.resolve(dataUrl);
    }

    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            try {
                const maxEdge = Math.max(img.width, img.height);
                const scale = maxEdge > SAVE_SNAPSHOT_MAX_EDGE_PX ? SAVE_SNAPSHOT_MAX_EDGE_PX / maxEdge : 1;
                const width = Math.max(1, Math.round(img.width * scale));
                const height = Math.max(1, Math.round(img.height * scale));
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    resolve(dataUrl);
                    return;
                }
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL('image/jpeg', SAVE_SNAPSHOT_JPEG_QUALITY));
            } catch {
                resolve(dataUrl);
            }
        };
        img.onerror = () => resolve(dataUrl);
        img.src = dataUrl;
    });
}

/** Compress each snapshot in a map sequentially; dedupe identical source URLs. */
export async function compressSnapshotMapForSave(
    snapshots: Record<string, string | undefined> | null | undefined,
    onProgress?: (completed: number, total: number) => void,
): Promise<Record<string, string>> {
    const entries = Object.entries(snapshots ?? {}).filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].trim().length > 0,
    );
    const out: Record<string, string> = {};
    const compressedBySource = new Map<string, string>();
    const total = entries.length;

    for (let index = 0; index < entries.length; index += 1) {
        const [key, source] = entries[index];
        let compressed = compressedBySource.get(source);
        if (!compressed) {
            compressed = await compressSnapshotDataUrlForSave(source);
            compressedBySource.set(source, compressed);
        }
        out[key] = compressed;
        onProgress?.(index + 1, total);
    }

    return out;
}

/** Store the initial top-down layout snapshot once the site model is ready. */
export function captureDefaultLayoutSnapshotIfNeeded(viewer: Viewer | null | undefined): void {
    if (!viewer || typeof viewer.getSquareImageOfSite !== 'function') return;
    if (!isSiteModelReadyForSnapshot(viewer)) return;

    const current = (useViewerStore.getState() as { layoutSnapshotsByKey?: Record<string, string> })
        .layoutSnapshotsByKey;
    if (isValidSnapshotDataUrl(current?.defaultInitial)) return;

    try {
        const img = viewer.getSquareImageOfSite(false);
        if (!isValidSnapshotDataUrl(img)) return;

        useViewerStore.setState((prev: { layoutSnapshotsByKey?: Record<string, string> }) => {
            const snaps = prev.layoutSnapshotsByKey ?? {};
            if (isValidSnapshotDataUrl(snaps.defaultInitial)) return prev;
            return {
                ...prev,
                layoutSnapshotsByKey: {
                    ...snaps,
                    defaultInitial: img,
                },
            };
        });
    } catch {
        // ignore capture errors – fallback paths will still work
    }
}
